export interface WhisperAudioMetrics {
  audioPeakLevel: number;
  audioVoicedSeconds: number;
}

export interface WhisperAudioRecorderOptions {
  stream: MediaStream;
  onInputLevel?: (level: number) => void;
  onSpeechStart?: () => void;
  onSpeechStop: (audio: Blob, metrics: WhisperAudioMetrics) => void;
  threshold?: number;
  silenceDurationMs?: number;
}

const DEFAULT_THRESHOLD = 0.12;
const DEFAULT_SILENCE_DURATION_MS = 1500;

export class WhisperAudioRecorder {
  private readonly options: WhisperAudioRecorderOptions;
  private readonly audioContext: AudioContext | null;
  private readonly analyser: AnalyserNode | null;
  private readonly samples: Uint8Array<ArrayBuffer> | null;
  private mediaRecorder: MediaRecorder | null = null;
  private chunks: BlobPart[] = [];
  private animationFrame: number | null = null;
  private silenceTimer: number | null = null;
  private recordingStartedAt = 0;
  private peakLevel = 0;
  private recording = false;
  private stopped = false;

  constructor(options: WhisperAudioRecorderOptions) {
    this.options = options;
    const AudioContextClass = window.AudioContext;
    if (!AudioContextClass) {
      this.audioContext = null;
      this.analyser = null;
      this.samples = null;
      return;
    }

    this.audioContext = new AudioContextClass();
    const source = this.audioContext.createMediaStreamSource(options.stream);
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 256;
    this.analyser.smoothingTimeConstant = 0.75;
    source.connect(this.analyser);
    this.samples = new Uint8Array(this.analyser.fftSize);
  }

  start() {
    if (this.stopped) return;
    if (typeof MediaRecorder === "undefined") {
      throw new Error("このブラウザは音声録音に対応していません。");
    }
    if (!this.analyser || !this.samples) {
      throw new Error("このブラウザは音声検出に対応していません。");
    }
    this.measure();
  }

  stop() {
    this.stopped = true;
    if (this.animationFrame !== null) {
      window.cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
    if (this.silenceTimer !== null) {
      window.clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }
    if (this.recording && this.mediaRecorder?.state === "recording") {
      this.mediaRecorder.stop();
    }
    this.recording = false;
    this.analyser?.disconnect();
    void this.audioContext?.close();
    this.options.onInputLevel?.(0);
  }

  private measure() {
    if (this.stopped || !this.analyser || !this.samples) return;

    this.analyser.getByteTimeDomainData(this.samples);
    let sumSquares = 0;
    for (const sample of this.samples) {
      const centered = (sample - 128) / 128;
      sumSquares += centered * centered;
    }
    const level = Math.min(1, Math.sqrt(sumSquares / this.samples.length) * 5);
    this.options.onInputLevel?.(level);
    this.peakLevel = Math.max(this.peakLevel, level);

    const now = performance.now();
    const threshold = this.options.threshold ?? DEFAULT_THRESHOLD;
    if (level >= threshold) {
      if (!this.recording) this.beginRecording(now);
      if (this.silenceTimer !== null) {
        window.clearTimeout(this.silenceTimer);
        this.silenceTimer = null;
      }
    } else if (this.recording && this.silenceTimer === null) {
      const silenceDuration = this.options.silenceDurationMs ?? DEFAULT_SILENCE_DURATION_MS;
      this.silenceTimer = window.setTimeout(() => {
        this.silenceTimer = null;
        this.finishRecording();
      }, silenceDuration);
    }

    this.animationFrame = window.requestAnimationFrame(() => this.measure());
  }

  private beginRecording(now: number) {
    if (typeof MediaRecorder === "undefined") return;

    const mimeType = MediaRecorder.isTypeSupported?.("audio/webm")
      ? "audio/webm"
      : undefined;
    this.mediaRecorder = mimeType
      ? new MediaRecorder(this.options.stream, { mimeType })
      : new MediaRecorder(this.options.stream);
    this.chunks = [];
    this.recordingStartedAt = now;
    this.peakLevel = 0;
    this.recording = true;
    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) this.chunks.push(event.data);
    };
    this.mediaRecorder.onstop = () => {
      const audio = new Blob(this.chunks, {
        type: this.mediaRecorder?.mimeType || "audio/webm",
      });
      const voicedSeconds = Math.max(
        0,
        (performance.now() - this.recordingStartedAt) / 1000,
      );
      this.chunks = [];
      this.mediaRecorder = null;
      this.recording = false;
      if (audio.size > 0 && !this.stopped) {
        this.options.onSpeechStop(audio, {
          audioPeakLevel: Number(this.peakLevel.toFixed(3)),
          audioVoicedSeconds: Number(voicedSeconds.toFixed(1)),
        });
      }
    };
    this.mediaRecorder.start(250);
    this.options.onSpeechStart?.();
  }

  private finishRecording() {
    if (!this.recording || !this.mediaRecorder || this.mediaRecorder.state !== "recording") return;
    this.mediaRecorder.stop();
  }
}
